import { AssetModal } from "@features/ax/shared/assetModal";
import { GenericTable } from "@shared/components/GenericTable";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { AlertTriangle } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface Asset {
  id: string;
  name: string;
  score: number;
  status: string;
}

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "critical":
      return "bg-red-100 text-red-800";
    case "warning":
      return "bg-yellow-100 text-yellow-800";
    case "maintenance":
      return "bg-orange-100 text-orange-800";
    case "active":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const OverviewTab: React.FC<{ data: any }> = ({ data }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { systemId } = useParams();

  const [showHighRiskAssetModal, setShowHighRiskAssetModal] = useState(false);
  const [showPoorConditionAssetModal, setShowPoorConditionAssetModal] =
    useState(false);

  const highRiskAssetFilters = {
    system: systemId,
    risk_status: ["Critical", "High"],
  };
  const poorConditionAssetFilters = {
    system: systemId,
    condition_status: ["Critical", "Poor"],
  };
  // Common columns for both tables
  const assetColumns: TableColumn<Asset>[] = useMemo(
    () => [
      {
        key: "assetName",
        header: "Asset Name",
        type: "text",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "score",
        header: "Score",
        type: "custom",
        render: (value: number) => (
          <span className="font-medium text-blue-600">{value}</span>
        ),
      },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: () => "secondary",
          getClassName: (value: string) => getStatusColor(value),
        },
        render: (value: string) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
    ],
    []
  );

  // Actions for both tables
  const assetActions: TableAction<Asset>[] = useMemo(
    () => [
      {
        label: "Schedule Inspection",
        onClick: (row: any) => {
          navigate(`/ax/service-order/landing/create`);
        },
        icon: "Calendar",
        variant: "outline",
      },
      {
        label: "Schedule Maintenance",
        onClick: (row: any) => {
          navigate(`/ax/service-order/landing/create`);
        },
        icon: "Wrench",
        variant: "outline",
      },
    ],
    [toast]
  );

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets in Poor Condition */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-5 h-5 bg-red-100 rounded flex items-center justify-center">
                <AlertTriangle className="h-3 w-3 text-red-600" />
              </div>
              <p className="page-title">Assets in Poor Condition</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-3">
            <GenericTable
              data={data?.assetsPoorCondition}
              columns={assetColumns}
              rowKey="id"
              actions={assetActions}
              emptyMessage="No assets found"
            />
            <div className="flex justify-center mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPoorConditionAssetModal(true)}
                className="text-sm"
              >
                View All
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Assets with Highest Risk */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
                <AlertTriangle className="h-3 w-3 text-orange-600" />
              </div>
              <p className="page-title">Assets with Highest Risk</p>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-3">
            <GenericTable
              data={data?.assetsHighestRisk}
              columns={assetColumns}
              rowKey="id"
              actions={assetActions}
              emptyMessage="No assets found"
            />
          </CardContent>
          <div className="flex justify-center mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHighRiskAssetModal(true)}
              className="text-sm"
            >
              View All
            </Button>
          </div>
        </Card>
      </div>
      <AssetModal
        showAssetsModal={showHighRiskAssetModal}
        setShowAssetsModal={setShowHighRiskAssetModal}
        filters={highRiskAssetFilters}
        title={"Assets with Highest Risk"}
      />
      <AssetModal
        showAssetsModal={showPoorConditionAssetModal}
        setShowAssetsModal={setShowPoorConditionAssetModal}
        filters={poorConditionAssetFilters}
        title={"asset in poor condition"}
      />
    </div>
  );
};
